import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Empty, Kpis, Modal, Panel, Segments, useFlash, useModal } from '../../ui/kit.js';
import { nextId } from '../../state/building.js';
import { CAMERAS, CAMERA_GROUPS, cameraById } from '../../data/cameras.js';
import { CameraTile } from '../../ui/cameras/camera-tile.js';
import { CameraViewer } from '../../ui/cameras/camera-viewer.js';
import { TalkButton } from '../../ui/cameras/controls.js';
import { useFullscreenSession } from '../../ui/cameras/fullscreen.js';
import { Timeline } from '../../ui/cameras/timeline.js';
import { ago, headOf, hms, overlayAbove, seekTo, useNow, ymd } from '../../ui/cameras/media.js';
import {
    BackIcon, ForwardIcon, FullscreenExitIcon, FullscreenIcon, GridIcon, NineIcon,
    PauseIcon, PlayIcon, SensorsIcon, SingleIcon, SnapshotIcon, VideocamIcon
} from '../../ui/cameras/icons.js';

const EASE = [0.16, 1, 0.3, 1];

/* The camera that starts the demo offline, so Reconnect has something
   to do. */
const OFFLINE_DEMO = 'cam-17';
const TOUR_MS = 8000;
const RETENTION_DAYS = 30;

const LAYOUTS = [
    { value: '1', label: <><SingleIcon aria-hidden="true" />1</> },
    { value: '4', label: <><GridIcon aria-hidden="true" />4</> },
    { value: '9', label: <><NineIcon aria-hidden="true" />9</> }
];

const INCIDENTS = [
    { id: 'IR-2417', title: 'Tailgating at the garage gate' },
    { id: 'IR-2416', title: 'Package room door propped open' },
    { id: 'IR-2413', title: 'Pool deck use after hours' }
];

/* What the analytics on each camera look for: vehicles on the two
   garage levels, people in public spaces, plain motion in plant rooms. */
const VEHICLE = new Set(['cam-12', 'cam-17']);
const PLANT = new Set(['cam-14']);
const detects = camera => (VEHICLE.has(camera.id) ? 'Vehicle' : PLANT.has(camera.id) ? 'Motion' : 'Person');

const rand = (lo, hi) => lo + Math.random() * (hi - lo);

function motionBox(camera) {
    const kind = detects(camera);
    const w = kind === 'Vehicle' ? rand(20, 30) : kind === 'Person' ? rand(8, 13) : rand(14, 22);
    const h = kind === 'Vehicle' ? w * rand(0.75, 0.95) : kind === 'Person' ? rand(30, 44) : rand(20, 30);
    return {
        id: nextId(),
        kind,
        x: rand(8, 92 - w),
        y: rand(kind === 'Person' ? 26 : 34, 92 - h),
        w,
        h,
        dx: rand(-45, 45),
        dy: rand(-5, 5),
        label: kind === 'Motion' ? 'Motion' : `${kind} ${Math.round(rand(84, 98))}%`
    };
}

/* The morning so far, relative to when the screen opened. */
function seedEvents(t0) {
    const at = (min, sec = 0) => t0 - (min * 60 + sec) * 1000;
    return [
        { cameraId: 'cam-12', kind: 'motion', title: 'Vehicle detected', at: at(1, 35) },
        { cameraId: 'cam-01', kind: 'motion', title: 'Person detected', at: at(4, 10) },
        { cameraId: 'cam-13', kind: 'door', title: 'Door held open', at: at(9, 40) },
        { cameraId: 'cam-07', kind: 'motion', title: 'Person detected', at: at(14, 5) },
        { cameraId: 'cam-11', kind: 'door', title: 'Stair door opened', at: at(21, 5) },
        { cameraId: OFFLINE_DEMO, kind: 'system', title: 'Signal lost', at: at(34, 20) },
        { cameraId: 'cam-05', kind: 'motion', title: 'Person detected', at: at(41) },
        { cameraId: 'cam-15', kind: 'door', title: 'Badge entry', at: at(52, 30) }
    ].filter(e => cameraById(e.cameraId)).map(e => ({ id: nextId(), ...e }));
}

const EVENT_TONE = { motion: 'info', door: 'warn', system: 'alert' };

/* Events are kept for the timeline's markers; the list shows the latest. */
const EVENT_KEEP = 40;
const EVENT_SHOWN = 9;

/**
 * Cameras: the security desk's video wall.
 *
 * Built like the VMS a security integrator would install: a 1, 4 or 9
 * camera wall filtered by area, a selected camera with its recording
 * underneath, snapshots, talk-down, a tour that cycles the cameras, and
 * the events the analytics raise, each one a tap away from the footage
 * that caused it. Everything is simulated, and all of it responds.
 */
function CamerasScreen() {
    const reduced = useReducedMotion();
    const [flash, notify] = useFlash();
    const [modalFlash, modalNotify] = useFlash();
    const snapModal = useModal();
    const [t0] = useState(() => Date.now());

    const [layout, setLayout] = useState('9');
    const [group, setGroup] = useState('All');
    const [query, setQuery] = useState('');
    const [focusId, setFocusId] = useState(CAMERAS[0].id);
    const [down, setDown] = useState(() => (cameraById(OFFLINE_DEMO) ? { [OFFLINE_DEMO]: t0 - (34 * 60 + 20) * 1000 } : {}));
    const [gaps, setGaps] = useState({});
    const [reconnecting, setReconnecting] = useState(null);
    const [playback, setPlayback] = useState({});
    const [motionOn, setMotionOn] = useState({});
    const [events, setEvents] = useState(() => seedEvents(t0));
    const [motionToday, setMotionToday] = useState(47);
    const [lastMotion, setLastMotion] = useState(() => t0 - 95000);
    const [snaps, setSnaps] = useState([]);
    const [flashes, setFlashes] = useState({});
    const [talkId, setTalkId] = useState(null);
    const [tour, setTour] = useState(null);
    const [viewerId, setViewerId] = useState(null);
    const [wallFull, setWallFull] = useState(false);
    const [incident, setIncident] = useState(INCIDENTS[0].id);

    const feeds = useRef({});
    const refCache = useRef({});
    const timers = useRef(new Set());
    const wallRef = useRef(null);

    /* Every timeout this screen starts, so leaving it cancels them all. */
    const later = useCallback((fn, ms) => {
        const id = setTimeout(() => { timers.current.delete(id); fn(); }, ms);
        timers.current.add(id);
    }, []);

    useEffect(() => {
        const pending = timers.current;
        return () => pending.forEach(clearTimeout);
    }, []);

    /* ── What the wall shows ─────────────────────────────────── */

    const inGroup = useMemo(() => CAMERAS.filter(c => group === 'All' || c.group === group), [group]);
    const focus = inGroup.find(c => c.id === focusId) || inGroup[0];
    const focusIndex = Math.max(0, inGroup.indexOf(focus));
    const size = Number(layout);
    const pageCount = Math.max(1, Math.ceil(inGroup.length / size));
    const page = Math.floor(focusIndex / size);

    /* The last page is filled from the end of the list rather than left
       mostly empty, and a group smaller than the layout gets a grid
       that fits it, so the wall is never a page of blank slots. */
    const startAt = size === 1 ? focusIndex : Math.max(0, Math.min(page * size, inGroup.length - size));
    const shown = inGroup.slice(startAt, startAt + size);
    const cols = size === 1 || shown.length <= 1 ? 1 : shown.length <= 4 ? 2 : 3;
    const rows = Math.ceil(shown.length / cols);
    const empty = cols > 1 ? (cols - (shown.length % cols)) % cols : 0;

    const gapsOf = useCallback(id => {
        const list = gaps[id] || [];
        return down[id] ? [...list, { from: down[id], to: null }] : list;
    }, [gaps, down]);

    const statusOf = useCallback(id => {
        if (reconnecting === id) return 'reconnecting';
        const pb = playback[id];
        if (pb) {
            const now = Date.now();
            const at = headOf(pb, now);
            return gapsOf(id).some(g => at >= g.from && at <= (g.to ?? now)) ? 'gap' : 'live';
        }
        return down[id] ? 'offline' : 'live';
    }, [reconnecting, playback, gapsOf, down]);

    const feedRef = (id, where) => {
        const key = `${where}:${id}`;
        if (!refCache.current[key]) {
            refCache.current[key] = el => {
                if (el) feeds.current[key] = el;
                else delete feeds.current[key];
            };
        }
        return refCache.current[key];
    };

    /* ── Full screen ─────────────────────────────────────────── */

    const fullscreen = useFullscreenSession(!!viewerId || wallFull, () => {
        setViewerId(null);
        setWallFull(false);
    });

    const expand = useCallback(id => {
        if (!wallFull) fullscreen.enter();
        setFocusId(id);
        setViewerId(id);
    }, [wallFull, fullscreen]);

    const closeViewer = useCallback(() => {
        setViewerId(null);
        if (!wallFull) fullscreen.leave();
    }, [wallFull, fullscreen]);

    const openWall = () => {
        fullscreen.enter();
        setWallFull(true);
    };

    const closeWall = useCallback(() => {
        setWallFull(false);
        fullscreen.leave();
    }, [fullscreen]);

    /* ── Controls ────────────────────────────────────────────── */

    const chooseGroup = next => {
        setGroup(next);
        const list = CAMERAS.filter(c => next === 'All' || c.group === next);
        if (!list.some(c => c.id === focusId)) setFocusId(list[0].id);
    };

    const chooseLayout = next => {
        if (tour) setTour(null);
        setLayout(next);
    };

    const turn = delta => {
        if (size === 1) {
            setFocusId(inGroup[(focusIndex + delta + inGroup.length) % inGroup.length].id);
            return;
        }
        const nextPage = (page + delta + pageCount) % pageCount;
        setFocusId(inGroup[Math.min(nextPage * size, inGroup.length - 1)].id);
    };

    const toggleTour = () => {
        if (tour) {
            setLayout(tour.from);
            setTour(null);
            notify('Tour stopped. The wall is back to its layout.');
        } else {
            setTour({ from: layout });
            setLayout('1');
            notify(`Touring ${inGroup.length} cameras, ${TOUR_MS / 1000} seconds on each.`);
        }
    };

    /* The tour steps to the next camera that has a picture, and waits
       while a camera is open full screen. */
    useEffect(() => {
        if (!tour || viewerId) return undefined;
        const timer = setTimeout(() => {
            const live = inGroup.filter(c => !down[c.id]);
            const at = live.findIndex(c => c.id === focus.id);
            const next = live[(at + 1) % live.length] || inGroup[0];
            setFocusId(next.id);
        }, TOUR_MS);
        return () => clearTimeout(timer);
    }, [tour, viewerId, focus.id, inGroup, down]);

    const setPlaybackFor = useCallback((id, next) => {
        setPlayback(p => {
            const copy = { ...p };
            if (next) copy[id] = next;
            else delete copy[id];
            return copy;
        });
    }, []);

    const talk = useCallback(camera => {
        const speaker = `${camera.name.toLowerCase()} speaker`;
        if (talkId === camera.id) {
            setTalkId(null);
            return `Talk-down ended. The ${speaker} is off.`;
        }
        setTalkId(camera.id);
        return `Speaking on the ${speaker}. Anyone nearby can hear you.`;
    }, [talkId]);

    const reconnect = useCallback(id => {
        const camera = cameraById(id);
        const since = down[id];
        setReconnecting(id);
        later(() => {
            const back = Date.now();
            setGaps(g => ({ ...g, [id]: [...(g[id] || []), { from: since, to: back }] }));
            setDown(d => {
                const copy = { ...d };
                delete copy[id];
                return copy;
            });
            setReconnecting(null);
            setEvents(list => [{ id: nextId(), cameraId: id, kind: 'system', title: 'Back online', at: back }, ...list].slice(0, EVENT_KEEP));
            notify(`${camera.name} is back online and recording.`);
        }, 2600);
        return `Reconnecting to ${camera.name}.`;
    }, [down, later, notify]);

    const reconnectTile = useCallback(id => notify(reconnect(id)), [notify, reconnect]);

    /* A snapshot keeps the frame and the moment it shows: the playhead's
       time when the camera is in playback, not the time it was taken. */
    const keep = useCallback((camera, shot) => {
        if (!shot) return `${camera.name} has no picture to capture right now.`;
        setSnaps(list => [{
            id: nextId(),
            cameraId: camera.id,
            name: camera.name,
            location: camera.location,
            at: shot.at,
            live: !playback[camera.id],
            src: shot.src,
            incident: null
        }, ...list].slice(0, 12));
        setFlashes(f => ({ ...f, [camera.id]: (f[camera.id] || 0) + 1 }));
        return `Snapshot of ${camera.name} saved to Snapshots.`;
    }, [playback]);

    const snapFocused = () => {
        const feed = statusOf(focus.id) === 'live' ? feeds.current[`${wallFull ? 'theater' : 'wall'}:${focus.id}`] : null;
        // capture() is { src, at } or null where a frame cannot be drawn.
        notify(keep(focus, feed ? feed.capture() : null));
    };

    const review = event => {
        const camera = cameraById(event.cameraId);
        if (!camera) return;
        if (!inGroup.includes(camera)) setGroup('All');
        setFocusId(camera.id);
        if (event.kind === 'system') {
            notify(`${camera.name}: ${event.title.toLowerCase()} at ${hms(event.at)}.`);
        } else {
            setPlaybackFor(camera.id, seekTo(event.at - 5000));
            notify(`Reviewing ${event.title.toLowerCase()} on ${camera.name} from ${hms(event.at - 5000)}.`);
        }
        /* On a phone the events are a long way below the wall. Scroll the
           screen's own scroller, not scrollIntoView, which would also
           scroll the kiosk frame around it. */
        const wall = wallRef.current;
        const scroller = wall && wall.closest('.screen__scroll');
        if (scroller) {
            const offset = wall.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
            if (offset < 0 || offset > scroller.clientHeight * 0.6) {
                scroller.scrollTo({ top: scroller.scrollTop + offset - 56, behavior: reduced ? 'auto' : 'smooth' });
            }
        }
    };

    /* ── The analytics: motion, now and then ─────────────────────
       Every few seconds one of the cameras on screen may see something.
       The box fades after a few seconds; a camera raises an event at
       most once every 45 seconds, so the list moves without flooding. */
    const watch = useRef(null);
    watch.current = { shown, viewerId, down, motionOn };
    const lastEvent = useRef({});

    useEffect(() => {
        const timer = setInterval(() => {
            const { shown: onWall, viewerId: open, down: offline, motionOn: busy } = watch.current;
            const pool = (open ? [cameraById(open)] : onWall).filter(c => c && !offline[c.id] && !busy[c.id]);
            if (!pool.length || Math.random() < 0.3) return;

            const camera = pool[Math.floor(Math.random() * pool.length)];
            const box = motionBox(camera);
            const at = Date.now();
            setMotionOn(m => ({ ...m, [camera.id]: box }));
            later(() => setMotionOn(m => {
                if (!m[camera.id] || m[camera.id].id !== box.id) return m;
                const copy = { ...m };
                delete copy[camera.id];
                return copy;
            }), 3400);
            setMotionToday(n => n + 1);
            setLastMotion(at);

            if (at - (lastEvent.current[camera.id] || 0) > 45000) {
                lastEvent.current[camera.id] = at;
                setEvents(list => [{ id: nextId(), cameraId: camera.id, kind: 'motion', title: `${box.kind} detected`, at }, ...list].slice(0, EVENT_KEEP));
            }
        }, 3800);
        return () => clearInterval(timer);
    }, [later]);

    /* Escape and the arrow keys on the full-screen wall. A camera open
       over it handles its own keys. */
    useEffect(() => {
        if (!wallFull) return undefined;
        const onKey = event => {
            if (viewerId || overlayAbove()) return;
            if (event.key === 'Escape') closeWall();
            else if (event.key === 'ArrowRight') turn(1);
            else if (event.key === 'ArrowLeft') turn(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    /* Attaching files the frame against a report: one already open, or a
       new one numbered after the last. */
    const attach = target => {
        const opened = incident === 'new';
        const id = opened ? `IR-${2418 + snaps.filter(s => s.incident && !INCIDENTS.some(i => i.id === s.incident)).length}` : incident;
        const named = INCIDENTS.find(i => i.id === id);
        setSnaps(list => list.map(s => (s.id === target.id ? { ...s, incident: id } : s)));
        modalNotify(named
            ? `Attached to ${id}, ${named.title.toLowerCase()}. The report now carries this frame.`
            : `Opened ${id} with this snapshot attached. Add the details from Incidents.`);
    };

    /* ── Derived figures ─────────────────────────────────────── */

    const offlineCount = Object.keys(down).length;
    const online = CAMERAS.length - offlineCount;
    const listed = inGroup.filter(c => {
        const q = query.trim().toLowerCase();
        return !q || `${c.name} ${c.location} ${c.id}`.toLowerCase().includes(q);
    });
    /* The modal keeps showing the last snapshot while it animates out. */
    const current = snaps.find(s => s.id === snapModal.item) || null;
    const lastSnap = useRef(null);
    if (current) lastSnap.current = current;
    const snap = current || lastSnap.current;
    const eventsFor = id => events.filter(e => e.cameraId === id);
    const groupName = group === 'All' ? 'All cameras' : group;

    const wallLabel = tour
        ? `Tour · ${focusIndex + 1} of ${inGroup.length}`
        : size === 1
            ? `${groupName} · ${focusIndex + 1} of ${inGroup.length}`
            : `${groupName} · ${startAt + 1}-${startAt + shown.length} of ${inGroup.length}`;

    const canTurn = size === 1 ? inGroup.length > 1 : pageCount > 1;

    const renderWall = where => (
        <div
            ref={where === 'wall' ? wallRef : undefined}
            className={`cam-wall cam-wall--c${cols}${where === 'theater' ? ' cam-wall--fit' : ''}`}
            style={{ '--cols': cols, '--rows': rows }}
        >
            {shown.map((camera, i) => (
                <CameraTile
                    key={camera.id}
                    ref={feedRef(camera.id, where)}
                    camera={camera}
                    index={i}
                    selected={size > 1 && camera.id === focus.id}
                    onSelect={setFocusId}
                    onExpand={expand}
                    status={statusOf(camera.id)}
                    since={down[camera.id]}
                    onReconnect={reconnectTile}
                    playback={playback[camera.id] || null}
                    motion={motionOn[camera.id] || null}
                    talking={talkId === camera.id}
                    flashKey={flashes[camera.id] || 0}
                    active={!viewerId}
                    priority={camera.id === focus.id ? 1 : 0}
                />
            ))}
            {Array.from({ length: empty }, (_, i) => (
                <div className="cam-slot" key={`slot-${i}`} aria-hidden="true">
                    <VideocamIcon />
                    <span>Empty slot</span>
                </div>
            ))}
            {tour && (
                <motion.span
                    key={`tour-${focus.id}`}
                    className="cam-tour"
                    aria-hidden="true"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: viewerId ? 0 : 1 }}
                    transition={{ duration: TOUR_MS / 1000, ease: 'linear' }}
                />
            )}
        </div>
    );

    const wallBar = (
        <div className="cam-wallbar">
            <span className="cam-wallbar__label">
                {tour && <span className="cam-wallbar__pulse" aria-hidden="true" />}
                {wallLabel}
            </span>
            {canTurn && (
                <div className="cam-wallbar__nav">
                    <button type="button" className="cam-ibtn cam-ibtn--sm" onClick={() => turn(-1)} aria-label={size === 1 ? 'Previous camera' : 'Previous page'}>
                        <BackIcon aria-hidden="true" />
                    </button>
                    <span className="cam-wallbar__page">
                        {size === 1 ? `${focusIndex + 1} / ${inGroup.length}` : `Page ${page + 1} of ${pageCount}`}
                    </span>
                    <button type="button" className="cam-ibtn cam-ibtn--sm" onClick={() => turn(1)} aria-label={size === 1 ? 'Next camera' : 'Next page'}>
                        <ForwardIcon aria-hidden="true" />
                    </button>
                </div>
            )}
        </div>
    );

    const tourButton = (
        <button
            type="button"
            className={`btn btn-sm ${tour ? 'btn-primary' : 'btn-outline-light'}`}
            aria-pressed={!!tour}
            onClick={toggleTour}
        >
            {tour ? <PauseIcon aria-hidden="true" /> : <PlayIcon aria-hidden="true" />}
            {tour ? 'Stop tour' : 'Tour'}
        </button>
    );

    const focusStatus = statusOf(focus.id);
    const focusPlayback = playback[focus.id] || null;

    return (
        <div className="screen-content cam-screen">
            <ScreenHead
                eyebrow="Security · Video"
                title="Security cameras"
                aside={(
                    <span className="cam-nvr">
                        <span className="cam-nvr__dot" aria-hidden="true" />
                        Recording to NVR · {RETENTION_DAYS}-day retention
                    </span>
                )}
            />

            <Kpis items={[
                {
                    label: 'Cameras online',
                    value: `${online}/${CAMERAS.length}`,
                    tone: offlineCount ? 'warn' : 'ok',
                    note: offlineCount ? `${offlineCount} offline` : 'All streams healthy'
                },
                { label: 'Recording', value: online, tone: 'info', note: 'Continuous, H.264' },
                { label: 'Motion today', value: motionToday, note: <Ago t={lastMotion} prefix="Last event " /> },
                { label: 'Storage', value: `${RETENTION_DAYS} days`, note: '18.4 of 24 TB used' }
            ]} />

            {flash}

            <div className="cam-toolbar">
                <Segments label="Layout" options={LAYOUTS} value={layout} onChange={chooseLayout} />
                <Segments label="Camera group" options={CAMERA_GROUPS} value={group} onChange={chooseGroup} />
                <div className="cam-toolbar__end">
                    {tourButton}
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={openWall} aria-label="Open the wall full screen">
                        <FullscreenIcon aria-hidden="true" />
                        <span className="cam-toolbar__fs-label">Full screen</span>
                    </button>
                </div>
            </div>

            <div className="cam-layout">
                <div className="cam-main">
                    {wallBar}
                    {wallFull ? (
                        <div className="cam-away">
                            <FullscreenIcon aria-hidden="true" />
                            <p>The wall is open full screen.</p>
                            <button type="button" className="btn btn-outline-light btn-sm" onClick={closeWall}>Bring it back</button>
                        </div>
                    ) : renderWall('wall')}

                    <Panel className="cam-focus" index={1}>
                        <div className="cam-focus__head">
                            <img className="cam-focus__thumb" src={focus.still} alt="" />
                            <div className="cam-focus__title">
                                <span className="cam-focus__eyebrow">Selected camera</span>
                                <h4>{focus.name}</h4>
                                <span className="row__meta">
                                    {focus.location} · {focus.id.toUpperCase()}{focus.ptz ? ' · PTZ' : ''}
                                </span>
                            </div>
                            <div className="cam-focus__actions">
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={snapFocused} disabled={focusStatus !== 'live'}>
                                    <SnapshotIcon aria-hidden="true" />
                                    Snapshot
                                </button>
                                <TalkButton on={talkId === focus.id} onClick={() => notify(talk(focus))} />
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => expand(focus.id)}>
                                    <FullscreenIcon aria-hidden="true" />
                                    Full screen
                                </button>
                            </div>
                        </div>
                        <Timeline
                            label={focus.name}
                            playback={focusPlayback}
                            onChange={next => setPlaybackFor(focus.id, next)}
                            events={eventsFor(focus.id)}
                            gaps={gapsOf(focus.id)}
                        />
                    </Panel>

                    <Panel className="cam-snaps" index={2}>
                        <div className="cam-panelhead">
                            <h4>Snapshots</h4>
                            <span className="row__meta">{snaps.length ? `${snaps.length} this shift` : 'None yet'}</span>
                        </div>
                        {snaps.length ? (
                            <ul className="cam-snaps__strip">
                                <AnimatePresence initial={false}>
                                    {snaps.map(s => (
                                        <motion.li
                                            key={s.id}
                                            layout={!reduced}
                                            initial={reduced ? false : { opacity: 0, scale: 0.8, x: -20 }}
                                            animate={{ opacity: 1, scale: 1, x: 0 }}
                                            transition={{ duration: 0.5, ease: EASE }}
                                        >
                                            <button type="button" className="cam-snap" onClick={() => { setIncident(INCIDENTS[0].id); snapModal.open(s.id); }}>
                                                <img src={s.src} alt="" />
                                                <span className="cam-snap__name">{s.name}</span>
                                                <span className="cam-snap__time">
                                                    {hms(s.at)}{s.live ? '' : ' · Rec'}
                                                    {s.incident && <span className="cam-snap__tag">{s.incident}</span>}
                                                </span>
                                            </button>
                                        </motion.li>
                                    ))}
                                </AnimatePresence>
                            </ul>
                        ) : (
                            <p className="cam-snaps__empty">
                                <SnapshotIcon aria-hidden="true" />
                                Snapshot any camera to keep a stamped still for an incident report or a shift handover.
                            </p>
                        )}
                    </Panel>
                </div>

                <aside className="cam-side">
                    <Panel className="cam-pick" index={3}>
                        <div className="cam-panelhead">
                            <h4>Cameras</h4>
                            <span className="row__meta">{online} of {CAMERAS.length} online</span>
                        </div>
                        <input
                            type="search"
                            className="form-control cam-pick__search"
                            placeholder="Find a camera"
                            aria-label="Find a camera"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                        {listed.length ? (
                            <ul className="cam-pick__list">
                                {listed.map(c => {
                                    const st = statusOf(c.id);
                                    const state = st === 'offline' ? 'Offline' : st === 'reconnecting' ? 'Connecting' : playback[c.id] ? 'Playback' : 'Live';
                                    return (
                                        <li key={c.id}>
                                            <button
                                                type="button"
                                                className={`cam-pick__item${c.id === focus.id ? ' cam-pick__item--on' : ''}`}
                                                aria-pressed={c.id === focus.id}
                                                onClick={() => setFocusId(c.id)}
                                                onDoubleClick={() => expand(c.id)}
                                            >
                                                <span className="cam-pick__thumb">
                                                    <img src={c.still} alt="" />
                                                    {motionOn[c.id] && <span className="cam-pick__motion" aria-hidden="true" />}
                                                </span>
                                                <span className="cam-pick__text">
                                                    <strong>{c.name}</strong>
                                                    <span>{c.location} · {c.id.toUpperCase()}</span>
                                                </span>
                                                <span className={`cam-pick__state cam-pick__state--${state.toLowerCase()}`}>
                                                    {state}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <Empty>No camera matches “{query}”.</Empty>
                        )}
                    </Panel>

                    <Panel className="cam-events" index={4}>
                        <div className="cam-panelhead">
                            <h4>Events</h4>
                            <span className="row__meta"><SensorsIcon className="cam-panelhead__icon" aria-hidden="true" />Analytics on</span>
                        </div>
                        <EventList events={events} onReview={review} reduced={reduced} />
                    </Panel>
                </aside>
            </div>

            <CameraViewer
                cameras={inGroup}
                cameraId={viewerId}
                onNavigate={id => { setViewerId(id); setFocusId(id); }}
                onClose={closeViewer}
                eyebrow={groupName}
                features={{ ptz: true, snapshot: true, playback: true, talk: true }}
                statusOf={statusOf}
                sinceOf={id => down[id]}
                onReconnect={reconnect}
                playbackOf={id => playback[id] || null}
                onPlayback={setPlaybackFor}
                eventsOf={eventsFor}
                gapsOf={gapsOf}
                motionOf={id => motionOn[id] || null}
                talkingId={talkId}
                onTalk={talk}
                onSnapshot={keep}
            />

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {wallFull && (
                        <motion.div
                            className="cam-theater"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Camera wall, full screen"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.35 }}
                        >
                            <div className="cam-theater__inner screen-content">
                                <header className="cam-theater__head">
                                    <div className="cam-viewer__titles">
                                        <span className="screen__eyebrow">Security wall</span>
                                        <h3 className="cam-viewer__title">{groupName}</h3>
                                    </div>
                                    <BigClock />
                                    <div className="cam-theater__controls">
                                        <Segments label="Layout" options={LAYOUTS} value={layout} onChange={chooseLayout} />
                                        {tourButton}
                                        <button type="button" className="cam-viewer__exit" onClick={closeWall} autoFocus>
                                            <FullscreenExitIcon aria-hidden="true" />
                                            <span>Exit</span>
                                        </button>
                                    </div>
                                </header>
                                {wallBar}
                                <div className="cam-theater__wall">{renderWall('theater')}</div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            <Modal
                open={!!current}
                onClose={snapModal.close}
                eyebrow={snap ? `Snapshot · ${snap.live ? 'Live' : 'From recording'}` : ''}
                title={snap ? snap.name : ''}
                size="lg"
                actions={snap && (
                    <>
                        <select
                            className="form-select cam-modal__incident"
                            aria-label="Incident report"
                            value={incident}
                            onChange={e => setIncident(e.target.value)}
                        >
                            {INCIDENTS.map(i => <option key={i.id} value={i.id}>{i.id} · {i.title}</option>)}
                            <option value="new">Open a new incident</option>
                        </select>
                        <a
                            className="btn btn-outline-light"
                            href={snap.src}
                            download={`${snap.cameraId}-${ymd(snap.at)}-${hms(snap.at).replace(/:/g, '')}.jpg`}
                        >
                            Download
                        </a>
                        <button type="button" className="btn btn-primary" onClick={() => attach(snap)}>
                            Attach to incident
                        </button>
                    </>
                )}
            >
                {modalFlash}
                {snap && (
                    <>
                        <img className="modal__media cam-modal__media" src={snap.src} alt={`${snap.name} at ${hms(snap.at)}`} />
                        <div className="cam-modal__meta">
                            <span className="row__meta">
                                {snap.location} · {snap.cameraId.toUpperCase()} · {ymd(snap.at)} {hms(snap.at)}
                            </span>
                            {snap.incident && <Chip tone="ok">Attached to {snap.incident}</Chip>}
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}

/* A time as "3 min ago", kept current by the shared clock. */
function Ago({ t, prefix = '' }) {
    const now = useNow();
    return <>{prefix}{ago(t, now).toLowerCase()}</>;
}

/* The wall's clock in full screen, large enough to read from the desk. */
function BigClock() {
    const now = useNow();
    return (
        <div className="cam-bigclock" aria-hidden="true">
            <strong>{hms(now)}</strong>
            <span>{ymd(now)}</span>
        </div>
    );
}

/**
 * What the analytics have raised, newest first. Each row opens the
 * footage: the camera is selected and played back from just before the
 * event, so "what was that?" is one tap.
 */
function EventList({ events, onReview, reduced }) {
    const now = useNow();

    return (
        <ul className="cam-eventlist">
            <AnimatePresence initial={false}>
                {events.slice(0, EVENT_SHOWN).map(event => {
                    const camera = cameraById(event.cameraId);
                    if (!camera) return null;
                    return (
                        <motion.li
                            key={event.id}
                            layout={!reduced}
                            initial={reduced ? false : { opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                        >
                            <button type="button" className="cam-event" onClick={() => onReview(event)}>
                                <img className="cam-event__thumb" src={camera.still} alt="" />
                                <span className="cam-event__text">
                                    <strong>
                                        <span className={`cam-event__dot cam-event__dot--${EVENT_TONE[event.kind] || 'info'}`} aria-hidden="true" />
                                        {event.title}
                                    </strong>
                                    <span>{camera.name} · {hms(event.at)}</span>
                                </span>
                                <span className="cam-event__ago">{ago(event.at, now)}</span>
                            </button>
                        </motion.li>
                    );
                })}
            </AnimatePresence>
        </ul>
    );
}

export default CamerasScreen;
